import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useBrands } from "../contexts/BrandContext";
import {
  Receipt,
  RotateCcw,
  ArrowUpRight,
  Filter,
  Inbox,
  User,
  Handshake,
  Store,
} from "lucide-react";

const money = (v) => {
  const n = Number(v || 0);
  return `${n.toFixed(2).replace(".", ",")} €`;
};

const FILTERS = [
  { id: "all", label: "Toutes", icon: Inbox },
  { id: "vente", label: "Ventes", icon: Receipt },
  { id: "remboursement", label: "Remb.", icon: RotateCcw },
];

const ORIGIN_META = {
  client: { label: "Client", icon: User, cls: "bg-blue-50 border-blue-200 text-blue-700" },
  partner: { label: "Partenaire", icon: Handshake, cls: "bg-emerald-50 border-emerald-200 text-emerald-700" },
  partner_site: { label: "Site partenaire", icon: Store, cls: "bg-amber-50 border-amber-200 text-amber-700" },
};

function dayLabel(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso + "T12:00:00").toLocaleDateString("fr-FR", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function BeneficiaireTransactions({ beneficiaryId }) {
  const { brands } = useBrands();
  const [data, setData] = useState({ items: [], linked_site_ids: [] });
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    if (!beneficiaryId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data: d } = await api.get(`/beneficiaries/${beneficiaryId}/transactions`);
        if (!cancelled) setData(d || { items: [], linked_site_ids: [] });
      } catch {
        if (!cancelled) setData({ items: [], linked_site_ids: [] });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [beneficiaryId]);

  const items = data.items || [];
  const linkedSites = data.linked_site_ids || [];

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    return items.filter((t) => (t.type || "vente") === filter);
  }, [items, filter]);

  const totals = useMemo(() => {
    let ventesTtc = 0;
    let rembTtc = 0;
    let ventesNet = 0;
    let rembNet = 0;
    let ventesCount = 0;
    let rembCount = 0;
    for (const t of items) {
      const ttc = Number(t.amount_ttc || 0);
      const net = Number(t.ca_net || 0);
      if ((t.type || "vente") === "remboursement") {
        rembTtc += ttc;
        rembNet += net;
        rembCount += 1;
      } else {
        ventesTtc += ttc;
        ventesNet += net;
        ventesCount += 1;
      }
    }
    return {
      ventes: ventesTtc,
      remb: rembTtc,
      net: ventesNet - rembNet,
      ventesCount,
      rembCount,
    };
  }, [items]);

  return (
    <div data-testid="beneficiaire-transactions">
      {/* Totaux */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4" data-testid="ben-tx-totals">
        <div className="bg-white border border-zinc-200 rounded-sm p-3">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Ventes</p>
          <p className="text-xl font-display font-bold text-emerald-700">{money(totals.ventes)}</p>
          <p className="text-[10px] font-mono text-zinc-500 mt-0.5">{totals.ventesCount} transaction{totals.ventesCount > 1 ? "s" : ""}</p>
        </div>
        <div className="bg-white border border-zinc-200 rounded-sm p-3">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Remboursements</p>
          <p className="text-xl font-display font-bold text-red-600">-{money(totals.remb)}</p>
          <p className="text-[10px] font-mono text-zinc-500 mt-0.5">{totals.rembCount} opération{totals.rembCount > 1 ? "s" : ""}</p>
        </div>
        <div className="bg-white border border-zinc-200 rounded-sm p-3">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">CA net</p>
          <p className="text-xl font-display font-bold text-zinc-900">{money(totals.net)}</p>
          <p className="text-[10px] font-mono text-zinc-500 mt-0.5">
            {linkedSites.length > 0 ? `${linkedSites.length} site(s) partenaire lié(s)` : "Aucun site lié"}
          </p>
        </div>
      </div>

      {/* Filtres type */}
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="ben-tx-filters">
        <Filter size={13} className="text-zinc-400" />
        {FILTERS.map((f) => {
          const Icon = f.icon;
          const count =
            f.id === "all"
              ? items.length
              : items.filter((t) => (t.type || "vente") === f.id).length;
          const active = filter === f.id;
          return (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              data-testid={`ben-tx-filter-${f.id}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border text-sm transition-colors ${
                active
                  ? "bg-zinc-900 text-white border-zinc-900"
                  : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-300"
              }`}
            >
              <Icon size={13} />
              {f.label}
              <span className={`ml-1 text-[10px] font-mono ${active ? "text-zinc-200" : "text-zinc-500"}`}>
                ({count})
              </span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="bg-white border border-zinc-200 rounded-sm p-8 text-center text-sm text-zinc-500 font-mono">
          Chargement…
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-sm p-8 text-center text-sm text-zinc-500 font-mono">
          Aucune transaction enregistrée
          {filter !== "all" ? ` (filtre : ${FILTERS.find((f) => f.id === filter)?.label})` : ""}.
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="ben-tx-table">
              <thead className="bg-zinc-50 text-[10px] uppercase font-mono tracking-wider text-zinc-500">
                <tr>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-left">Département</th>
                  <th className="px-3 py-2 text-left">Site</th>
                  <th className="px-3 py-2 text-left">Service / produit</th>
                  <th className="px-3 py-2 text-left">Rôle</th>
                  <th className="px-3 py-2 text-right">TTC</th>
                  <th className="px-3 py-2 text-right">CA net</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {filtered.map((t) => {
                  const br = brands.find((x) => x.id === t.brand_id);
                  const isRefund = (t.type || "vente") === "remboursement";
                  const origins = t.link_origins || [];
                  return (
                    <tr key={t.id} data-testid={`ben-tx-row-${t.id}`} className="hover:bg-zinc-50/60">
                      <td className="px-3 py-2 whitespace-nowrap text-xs font-mono text-zinc-700">
                        {dayLabel(t.date)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm ${
                            isRefund
                              ? "bg-red-50 border border-red-100 text-red-700"
                              : "bg-emerald-50 border border-emerald-100 text-emerald-700"
                          }`}
                        >
                          {isRefund ? <RotateCcw size={10} /> : <Receipt size={10} />}
                          {isRefund ? "Remb." : "Vente"}
                        </span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {br ? (
                          <span className="inline-flex items-center gap-1.5 text-[11px] font-mono">
                            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: br.color }} />
                            {br.name}
                          </span>
                        ) : (
                          <span className="text-zinc-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-zinc-700">
                        {t.site_name || <span className="text-zinc-400">—</span>}
                      </td>
                      <td className="px-3 py-2 text-xs text-zinc-700 max-w-[240px] truncate" title={t.service_product_name}>
                        {t.service_product_name || <span className="text-zinc-400">—</span>}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="flex flex-wrap gap-1">
                          {origins.length === 0 && <span className="text-zinc-400 text-xs">—</span>}
                          {origins.map((o) => {
                            const meta = ORIGIN_META[o];
                            if (!meta) return null;
                            const Icon = meta.icon;
                            return (
                              <span
                                key={o}
                                className={`inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded-sm border ${meta.cls}`}
                                data-testid={`ben-tx-origin-${t.id}-${o}`}
                              >
                                <Icon size={10} />
                                {meta.label}
                              </span>
                            );
                          })}
                        </div>
                      </td>
                      <td className={`px-3 py-2 text-right whitespace-nowrap font-mono text-sm font-semibold ${isRefund ? "text-red-600" : "text-zinc-900"}`}>
                        {isRefund ? "-" : ""}
                        {money(t.amount_ttc)}
                      </td>
                      <td className={`px-3 py-2 text-right whitespace-nowrap font-mono text-xs ${isRefund ? "text-red-500" : "text-zinc-600"}`}>
                        {isRefund ? "-" : ""}
                        {money(t.ca_net)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-3 py-2 border-t border-zinc-100 text-[10px] font-mono text-zinc-500 flex items-center gap-2 flex-wrap">
            <ArrowUpRight size={11} />
            Historique de toutes les transactions liées à ce contact (client, partenaire ou site partenaire associé).
          </div>
        </div>
      )}
    </div>
  );
}
